import type { CSSProperties } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/hooks/useI18n';
import { useShell } from '@/hooks/useShell';
import { getRouteForRoleOrCategory } from '@/lib/roleRoutes';
import { cabinetNavigateOptions } from '@/lib/navigationState';
import { routes } from '@/lib/routes';
import type { RoleCode } from '@/types';

/** NEW-VERSION-1.83 personalCodes — only these appear under «Мои роли». */
const PERSONAL_ROLE_KEYS = new Set([
  'SYSTEM_OWNER',
  'ADVISOR',
  'SUPERVISOR',
  'TEACHER',
  'RESEARCHER',
  'ALL_USERS',
]);

/** Icons mirrored from NEW-VERSION-1.83 categoryRegistry / personalRegistry. */
const CATEGORY_ICONS: Record<string, string> = {
  OPERATIONS_PARTNER: '⚙',
  RESEARCH_PARTNER: '🤝',
  MENTOR_PARTNER: '🤝',
  DEPARTMENT_HEAD: '🏛',
  DEPARTMENT_ASSISTANT: '▤',
  DEPARTMENT_LAB: '▤',
  DOCTORAL_STUDENT: '🎓',
  LEARNER: '🎓',
  SUPERVISED_STUDENT: '◈',
  STUDENT: '◈',
  TEACHER: '▦',
  COURSE_STUDENT: '◇',
  AUTHOR: '✎',
  CLIENT: '●',
  PROJECT_EDITOR: '▧',
  EDITOR: '▧',
  PROJECT_MEMBER: '⌘',
};

const ROLE_ICONS: Record<string, string> = {
  SYSTEM_OWNER: '♛',
  ADVISOR: '⌖',
  SUPERVISOR: '⚗',
  TEACHER: '▦',
  RESEARCHER: '▦',
  ALL_USERS: '👥',
};

const CATEGORY_ACCENTS = [
  '#49ddb7',
  '#9e80ff',
  '#e7b13a',
  '#f2bf65',
  '#41d2ff',
  '#66b7e8',
  '#41d2ff',
  '#49ddb7',
  '#e7b13a',
  '#49ddb7',
  '#ff7b65',
  '#9e80ff',
];

const pathMatchesCabinet = (pathname: string, href: string | undefined): boolean => {
  if (!href) return false;
  return pathname === href || pathname.startsWith(`${href}/`);
};

/**
 * NEW-VERSION-1.83 R515: category click switches to that cabinet interface
 * (owner preview / assigned workspace). Personal roles open assigned cabinets.
 */
export const CategoryRolesStrip = () => {
  const { shell } = useShell();
  const { locale } = useI18n();
  const { user, switchRole, hasRole } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const categories = shell?.categories ?? [];
  const roles = (shell?.roles ?? []).filter((role) => {
    if (!PERSONAL_ROLE_KEYS.has(role.key)) return false;
    if (!user) return false;
    if (role.key === 'ALL_USERS') return hasRole('SYSTEM_OWNER');
    return hasRole(role.key as RoleCode);
  });

  const isOwner = hasRole('SYSTEM_OWNER');

  if (!categories.length && !roles.length) return null;

  return (
    <section className="r515-switch-shell" aria-label="Категории и роли платформы">
      {categories.length > 0 ? (
        <div className="r515-switch-row">
          <div className="r515-switch-label">
            <span aria-hidden="true">▦</span>
            <div>
              <b>Категории платформы</b>
              <small>
                {isOwner
                  ? 'Предпросмотр кабинета выбранной категории'
                  : 'Доступные вам направления платформы'}
              </small>
            </div>
          </div>
          <div className="r515-category-scroller">
            {categories.map((category, index) => {
              const accent = CATEGORY_ACCENTS[index % CATEGORY_ACCENTS.length];
              const href = getRouteForRoleOrCategory(category.code) ?? undefined;
              const icon = CATEGORY_ICONS[category.code] ?? '◆';
              const isActive = pathMatchesCabinet(pathname, href);
              return (
                <button
                  key={category.code}
                  type="button"
                  className={`r515-category-card${isActive ? ' active' : ''}`}
                  style={{ '--accent': accent } as CSSProperties}
                  data-r515-category={category.code}
                  aria-pressed={isActive}
                  onClick={() => {
                    if (!user) {
                      navigate(routes.login);
                      return;
                    }
                    // Template: every category card opens its cabinet interface.
                    // Owner: preview only — keep SYSTEM_OWNER identity (no impersonation).
                    if (href) {
                      navigate(
                        href,
                        cabinetNavigateOptions(href, {
                          category: category.code,
                          role: isOwner ? (user.activeRole || 'SYSTEM_OWNER') : category.code,
                          preview: isOwner ? category.code : undefined,
                          currentState: undefined,
                        }),
                      );
                    }
                  }}
                >
                  <span className="r515-category-icon" aria-hidden="true">
                    {icon}
                  </span>
                  <span>
                    <strong>{category.label[locale]}</strong>
                    <small>{category.caption[locale]}</small>
                  </span>
                  <b className="r515-zero" style={category.counter > 0 ? { color: accent } : undefined}>
                    {category.counter}
                  </b>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {roles.length > 0 ? (
        <div className="r515-switch-row">
          <div className="r515-switch-label">
            <span aria-hidden="true">♛</span>
            <div>
              <b>Мои роли</b>
              <small>Назначенные кабинеты текущего аккаунта</small>
            </div>
          </div>
          <div className="r515-personal-grid">
            {roles.map((role) => {
              const href =
                role.key === 'ALL_USERS'
                  ? `${routes.owner}/users`
                  : (getRouteForRoleOrCategory(role.key) ?? routes.home);
              const isSelected =
                Boolean(user && user.activeRole === role.key) || pathMatchesCabinet(pathname, href);
              const icon = ROLE_ICONS[role.key] ?? '●';
              return (
                <button
                  key={role.key}
                  type="button"
                  className={`r515-personal-card${isSelected ? ' active' : ''}`}
                  style={{ '--accent': role.accent } as CSSProperties}
                  data-r515-personal={role.key}
                  aria-pressed={isSelected}
                  onClick={() => {
                    if (!user) {
                      navigate(routes.login);
                      return;
                    }
                    if (role.key === 'ALL_USERS') {
                      if (hasRole('SYSTEM_OWNER')) {
                        navigate(
                          href,
                          cabinetNavigateOptions(href, {
                            role: 'SYSTEM_OWNER',
                          }),
                        );
                      }
                      return;
                    }
                    if (hasRole(role.key as RoleCode)) {
                      switchRole(role.key as RoleCode);
                      navigate(
                        href,
                        cabinetNavigateOptions(href, {
                          role: role.key,
                        }),
                      );
                    }
                  }}
                >
                  <span className="r515-personal-icon" aria-hidden="true">
                    {icon}
                  </span>
                  <span>
                    <strong>{role.label[locale]}</strong>
                    <small>{role.caption[locale]}</small>
                  </span>
                  <b className="r515-badge">{role.counter}</b>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </section>
  );
};
